import { createServerFn } from "@tanstack/react-start";

export type SponsorTier =
  | "hoofdpartner"
  | "premium"
  | "partner"
  | "supporter"
  | "vriend";

export const SPONSOR_TIERS: SponsorTier[] = [
  "hoofdpartner",
  "premium",
  "partner",
  "supporter",
  "vriend",
];

export type PublicSponsor = {
  id: string;
  company: string;
  logo_url: string | null;
  website_url: string | null;
  tier: SponsorTier;
  bg_variant: "light" | "dark" | "transparent";
  invert_on_dark: boolean;
};

export const getPublicSponsors = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicSponsor[]> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("sponsors", 300_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data, error } = await supabaseAdmin.rpc("get_public_sponsors");
    if (error || !data) return [];

    const results: PublicSponsor[] = [];
    for (const row of data as Array<{
      id: string;
      company: string;
      logo_url: string | null;
      website_url: string | null;
      tier: string | null;
      bg_variant: string | null;
      invert_on_dark: boolean | null;
    }>) {
      if (!row.logo_url) continue;
      const tier = (SPONSOR_TIERS as string[]).includes(row.tier ?? "")
        ? (row.tier as SponsorTier)
        : "partner";
      const bg_variant = (row.bg_variant === "dark" || row.bg_variant === "transparent" ? row.bg_variant : "light") as PublicSponsor["bg_variant"];
      const invert_on_dark = !!row.invert_on_dark;
      if (/^https?:\/\//i.test(row.logo_url)) {
        results.push({ id: row.id, company: row.company, logo_url: row.logo_url, website_url: row.website_url ?? null, tier, bg_variant, invert_on_dark });
        continue;
      }
      const { data: signed } = await supabaseAdmin.storage
        .from("sponsor-logos")
        .createSignedUrl(row.logo_url, 60 * 60);
      results.push({
        id: row.id,
        company: row.company,
        logo_url: signed?.signedUrl ?? null,
        website_url: row.website_url ?? null,
        tier,
        bg_variant,
        invert_on_dark,
      });
    }
    return results;
    });
  },
);